import { useState } from "react";
import { useFormContext, useFieldArray } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Plus,
  Trash2,
  Edit,
  GripVertical,
  Package,
  Wrench,
  Copy,
  ArrowUp,
  ArrowDown,
  Calculator
} from "lucide-react";
import { BudgetFormData, BudgetItem } from "@/types/budget";

interface BudgetItemsSectionProps {
  services: Array<{ id: string; name: string; price: number; unit: string; description?: string }>;
  products: Array<{ id: string; name: string; price: number; stock?: number; description?: string }>;
  categories: Array<{ id: string; name: string; type: string }>;
  costCenters: Array<{ id: string; name: string }>;
  className?: string;
}

interface ItemDialogProps {
  isOpen: boolean;
  onClose: () => void;
  editingIndex?: number;
  defaultValues?: Partial<BudgetItem>;
}

export function BudgetItemsSection({
  services,
  products,
  categories,
  costCenters,
  className = ""
}: BudgetItemsSectionProps) {
  const { control, watch, setValue } = useFormContext<BudgetFormData>();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "items"
  });

  const [itemDialog, setItemDialog] = useState<ItemDialogProps>({
    isOpen: false
  });

  const items = watch("items") || [];

  const addItem = (item: Partial<BudgetItem>) => {
    const newItem: BudgetItem = {
      description: item.description || "",
      quantity: item.quantity || 1,
      unit_value: item.unit_value || 0,
      total_value: (item.quantity || 1) * (item.unit_value || 0),
      item_type: item.item_type || "service",
      service_id: item.service_id || "",
      product_id: item.product_id || "",
      category_id: item.category_id || "",
      cost_center_id: item.cost_center_id || "",
      notes: item.notes || ""
    };

    if (itemDialog.editingIndex !== undefined) {
      // Update existing item
      setValue(`items.${itemDialog.editingIndex}`, newItem);
    } else {
      // Add new item
      append(newItem);
    }

    setItemDialog({ isOpen: false });
  };

  const removeItem = (index: number) => {
    remove(index);
  };

  const moveItem = (index: number, direction: "up" | "down") => {
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < fields.length) {
      move(index, newIndex);
    }
  };

  const duplicateItem = (index: number) => {
    const itemToDuplicate = items[index];
    const duplicatedItem = {
      ...itemToDuplicate,
      description: `${itemToDuplicate.description} (Cópia)`
    };
    append(duplicatedItem);
  };

  const openEditDialog = (index: number) => {
    setItemDialog({
      isOpen: true,
      editingIndex: index,
      defaultValues: items[index]
    });
  };

  const calculateItemTotal = (quantity: number, unitValue: number) => {
    return quantity * unitValue;
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + (item.total_value || 0), 0);
  };

  return (
    <div className={`space-y-6 ${className}`}>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Itens do Orçamento
              </CardTitle>
              <CardDescription>
                Adicione serviços e produtos ao orçamento
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">
                {items.length} {items.length === 1 ? "item" : "itens"}
              </Badge>
              <Badge variant="secondary">
                Total: {new Intl.NumberFormat("pt-BR", {
                  style: "currency",
                  currency: "BRL"
                }).format(calculateSubtotal())}
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-muted rounded-lg">
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">Nenhum item adicionado</h3>
              <p className="text-muted-foreground mb-4">
                Adicione serviços ou produtos para criar seu orçamento
              </p>
              <Button onClick={() => setItemDialog({ isOpen: true })}>
                <Plus className="h-4 w-4 mr-2" />
                Adicionar Primeiro Item
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-end">
                <Button onClick={() => setItemDialog({ isOpen: true })}>
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar Item
                </Button>
              </div>

              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40px]"></TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead className="text-right">Quantidade</TableHead>
                      <TableHead className="text-right">Valor Unit.</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => moveItem(index, "up")}
                              disabled={index === 0}
                            >
                              <ArrowUp className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => moveItem(index, "down")}
                              disabled={index === items.length - 1}
                            >
                              <ArrowDown className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{item.description}</div>
                            {item.notes && (
                              <div className="text-sm text-muted-foreground">
                                {item.notes}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={item.item_type === "service" ? "default" : "secondary"}>
                            {item.item_type === "service" ? (
                              <>
                                <Wrench className="h-3 w-3 mr-1" />
                                Serviço
                              </>
                            ) : (
                              <>
                                <Package className="h-3 w-3 mr-1" />
                                Produto
                              </>
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {item.quantity}
                        </TableCell>
                        <TableCell className="text-right">
                          {new Intl.NumberFormat("pt-BR", {
                            style: "currency",
                            currency: "BRL"
                          }).format(item.unit_value)}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {new Intl.NumberFormat("pt-BR", {
                            style: "currency",
                            currency: "BRL"
                          }).format(item.total_value)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => openEditDialog(index)}
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => duplicateItem(index)}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                              onClick={() => removeItem(index)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Subtotal Summary */}
              <div className="flex justify-end">
                <div className="text-right">
                  <div className="text-sm text-muted-foreground">Subtotal</div>
                  <div className="text-2xl font-bold">
                    {new Intl.NumberFormat("pt-BR", {
                      style: "currency",
                      currency: "BRL"
                    }).format(calculateSubtotal())}
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Item Dialog */}
      <ItemDialog
        isOpen={itemDialog.isOpen}
        onClose={() => setItemDialog({ isOpen: false })}
        editingIndex={itemDialog.editingIndex}
        defaultValues={itemDialog.defaultValues}
        services={services}
        products={products}
        categories={categories}
        costCenters={costCenters}
        onSave={addItem}
      />
    </div>
  );
}

// Item Dialog Component
interface ItemDialogComponentProps extends ItemDialogProps {
  services: Array<{ id: string; name: string; price: number; unit: string; description?: string }>;
  products: Array<{ id: string; name: string; price: number; stock?: number; description?: string }>;
  categories: Array<{ id: string; name: string; type: string }>;
  costCenters: Array<{ id: string; name: string }>;
  onSave: (item: Partial<BudgetItem>) => void;
}

function ItemDialog({
  isOpen,
  onClose,
  editingIndex,
  defaultValues,
  services,
  products,
  categories,
  costCenters,
  onSave
}: ItemDialogComponentProps) {
  const [itemType, setItemType] = useState<"service" | "product">(defaultValues?.item_type || "service");
  const [formData, setFormData] = useState<Partial<BudgetItem>>({
    description: "",
    quantity: 1,
    unit_value: 0,
    item_type: "service",
    service_id: "",
    product_id: "",
    category_id: "",
    cost_center_id: "",
    notes: "",
    ...defaultValues
  });

  const calculateTotal = () => {
    const total = (formData.quantity || 0) * (formData.unit_value || 0);
    setFormData(prev => ({ ...prev, total_value: total }));
  };

  const handleServiceSelect = (serviceId: string) => {
    const service = services.find(s => s.id === serviceId);
    if (service) {
      setFormData(prev => ({
        ...prev,
        service_id: serviceId,
        description: service.name,
        unit_value: service.price,
        product_id: ""
      }));
      calculateTotal();
    }
  };

  const handleProductSelect = (productId: string) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      setFormData(prev => ({
        ...prev,
        product_id: productId,
        description: product.name,
        unit_value: product.price,
        service_id: ""
      }));
      calculateTotal();
    }
  };

  const handleSubmit = () => {
    if (!formData.description || !formData.quantity || !formData.unit_value) {
      return;
    }

    onSave(formData);
  };

  const handleClose = () => {
    setFormData({
      description: "",
      quantity: 1,
      unit_value: 0,
      item_type: "service",
      service_id: "",
      product_id: "",
      category_id: "",
      cost_center_id: "",
      notes: ""
    });
    setItemType("service");
    onClose();
  };

  const availableCategories = categories.filter(cat => cat.type === itemType);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingIndex !== undefined ? "Editar Item" : "Adicionar Item"}
          </DialogTitle>
          <DialogDescription>
            {editingIndex !== undefined
              ? "Edite as informações do item selecionado"
              : "Adicione um serviço ou produto ao orçamento"
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Item Type Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Tipo de Item</label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={itemType === "service" ? "default" : "outline"}
                onClick={() => setItemType("service")}
                className="flex-1"
              >
                <Wrench className="h-4 w-4 mr-2" />
                Serviço
              </Button>
              <Button
                type="button"
                variant={itemType === "product" ? "default" : "outline"}
                onClick={() => setItemType("product")}
                className="flex-1"
              >
                <Package className="h-4 w-4 mr-2" />
                Produto
              </Button>
            </div>
          </div>

          {/* Service/Product Selection */}
          {itemType === "service" ? (
            <div className="space-y-2">
              <label className="text-sm font-medium">Selecionar Serviço</label>
              <Select
                value={formData.service_id || ""}
                onValueChange={handleServiceSelect}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um serviço (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((service) => (
                    <SelectItem key={service.id} value={service.id}>
                      <div className="flex flex-col items-start">
                        <span className="font-medium">{service.name}</span>
                        <span className="text-sm text-muted-foreground">
                          {new Intl.NumberFormat("pt-BR", {
                            style: "currency",
                            currency: "BRL"
                          }).format(service.price)} / {service.unit}
                        </span>
                        {service.description && (
                          <span className="text-xs text-muted-foreground">
                            {service.description}
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="space-y-2">
              <label className="text-sm font-medium">Selecionar Produto</label>
              <Select
                value={formData.product_id || ""}
                onValueChange={handleProductSelect}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um produto (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  {products.map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      <div className="flex flex-col items-start">
                        <span className="font-medium">{product.name}</span>
                        <span className="text-sm text-muted-foreground">
                          {new Intl.NumberFormat("pt-BR", {
                            style: "currency",
                            currency: "BRL"
                          }).format(product.price)}
                          {product.stock !== undefined && (
                            <span className="text-xs text-muted-foreground">
                              Estoque: {product.stock}
                            </span>
                          )}
                        </span>
                        {product.description && (
                          <span className="text-xs text-muted-foreground">
                            {product.description}
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Manual Description */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Descrição *</label>
            <Input
              value={formData.description || ""}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Descrição detalhada do item"
            />
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {/* Quantity */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Quantidade *</label>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                value={formData.quantity || ""}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 0;
                  setFormData(prev => ({ ...prev, quantity: value }));
                  calculateTotal();
                }}
              />
            </div>

            {/* Unit Value */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Valor Unitário *</label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={formData.unit_value || ""}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 0;
                  setFormData(prev => ({ ...prev, unit_value: value }));
                  calculateTotal();
                }}
              />
            </div>

            {/* Total (calculated) */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Total</label>
              <div className="flex items-center gap-2 h-10 px-3 py-2 border rounded-md bg-muted">
                <Calculator className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">
                  {new Intl.NumberFormat("pt-BR", {
                    style: "currency",
                    currency: "BRL"
                  }).format(formData.total_value || 0)}
                </span>
              </div>
            </div>
          </div>

          {/* Category and Cost Center */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">Categoria</label>
              <Select
                value={formData.category_id || ""}
                onValueChange={(value) => setFormData(prev => ({ ...prev, category_id: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma categoria" />
                </SelectTrigger>
                <SelectContent>
                  {availableCategories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Centro de Custo</label>
              <Select
                value={formData.cost_center_id || ""}
                onValueChange={(value) => setFormData(prev => ({ ...prev, cost_center_id: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um centro de custo" />
                </SelectTrigger>
                <SelectContent>
                  {costCenters.map((center) => (
                    <SelectItem key={center.id} value={center.id}>
                      {center.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Observações</label>
            <Textarea
              value={formData.notes || ""}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Observações adicionais sobre este item"
              rows={3}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={handleClose}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit}>
            {editingIndex !== undefined ? "Salvar Alterações" : "Adicionar Item"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}